"use client";

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { track } from "@vercel/analytics";
import { FileText, Shield, Target, Gift, ChevronDown, Check, ExternalLink, User, CheckCircle, Gavel } from "lucide-react";
import Image from "next/image";
import { acceptTerms } from "@/lib/actions";
import { toast } from "sonner";
import { reportError } from "@/lib/report-error";

interface OnboardingScreenProps {
  onAccept: () => void;
}

export function OnboardingScreen({ onAccept }: OnboardingScreenProps) {
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Consider "scrolled to bottom" when within 20px of the bottom
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
      setHasScrolledToBottom(isAtBottom);
    };

    // Check if content is already at the bottom (no scrolling needed)
    if (container.scrollHeight <= container.clientHeight) {
      setHasScrolledToBottom(true);
    }

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const handleContinue = async () => {
    if (!isChecked || isSubmitting) return;
    
    track("terms_accepted");
    setIsSubmitting(true);
    try {
      const result = await acceptTerms();

      if (result.success) {
        onAccept();
      } else {
        console.error("Failed to accept terms:", result.error);
        toast.error("Failed to accept terms. Please try again.");
        setIsSubmitting(false);
      }
    } catch (error) {
      reportError(error, { component: "OnboardingScreen", action: "handleContinue" });
      toast.error("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-gradient-to-b from-[hsl(217,50%,21%)] to-[hsl(223,54%,31%)]">
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overscroll-contain px-4 pt-safe landscape:px-safe-or-4">
        {/* Header - logo/wordmark centered, title centered below on narrow, inline on wide */}
        <div className="flex flex-col items-center gap-2 pb-4 pt-4 sm:flex-row sm:justify-center sm:gap-4">
          {/* Logo and wordmark */}
          <div className="flex items-center gap-3">
            <Image
              src="https://assets.fluidityiq.ai/assets/fiq-logo-icon-small.png"
              alt="FluidityIQ"
              width={40}
              height={40}
              className="h-10 w-10"
            />
            <Image
              src="https://assets.fluidityiq.ai/assets/fiq-logo.png"
              alt="FluidityIQ"
              width={120}
              height={32}
              className="-mb-[10px] -ml-[10px] h-8"
              style={{ width: 'auto' }}
            />
          </div>
          {/* Title - centered, wraps to second line on narrow screens */}
          <h1 className="text-center text-xl font-bold text-white sm:text-2xl">
            Design Patent Scavenger Hunt
          </h1>
        </div>

        <div className="flex flex-col gap-4 pb-4">
          {/* How it Works */}
          <Card className="rounded-2xl border-0 bg-white/10 text-white backdrop-blur-sm">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-lg text-white">
                <Target className="h-5 w-5 text-primary" />
                How It Works
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-base font-medium leading-relaxed tracking-wide text-white/95">
              <div className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">1</span>
                <span>Each day, you&apos;ll be able to access a list of items to photograph and match to a Design Patent.</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">2</span>
                <span>You&apos;ll use a simplified version of FluidityIQ Design Patent search technology (US data only) to accomplish the task.</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">3</span>
                <span>The items will be easy to locate in and around the event venue.</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">4</span>
                <span>Complete the scavenger hunt each day to maximize your chances of winning.</span>
              </div>
            </CardContent>
          </Card>

          {/* Rules */}
          <Card className="rounded-2xl border-0 bg-white/10 text-white backdrop-blur-sm">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-lg text-white">
                <FileText className="h-5 w-5 text-primary" />
                Rules
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-base font-medium leading-relaxed tracking-wide text-white/95">
              <div className="flex items-start gap-3">
                <User className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>One entry per person per day</span>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>Submissions are verified for reasonableness by FluidityIQ</span>
              </div>
              <div className="flex items-start gap-3">
                <Gavel className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>All decisions are final</span>
              </div>
            </CardContent>
          </Card>

          {/* Prizes */}
          <Card className="rounded-2xl border-0 bg-white/10 text-white backdrop-blur-sm">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-lg text-white">
                <Gift className="h-5 w-5 text-primary" />
                Prizes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-base font-medium leading-relaxed tracking-wide text-white/95">
              <p>At the close of each conference day, a winner will be selected from those who have completed the hunt and contacted via email.</p>
              <p>Attendees still onsite can pickup their prize by presenting the email at the FluidityIQ booth.</p>
              <p>Winners that are no longer in attendance will be contacted after the conference is over.</p>
            </CardContent>
          </Card>

          {/* Privacy & Terms */}
          <Card className="rounded-2xl border-0 bg-white/10 text-white backdrop-blur-sm">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-lg text-white">
                <Shield className="h-5 w-5 text-primary" />
                Privacy & Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="text-base font-medium leading-relaxed tracking-wide text-white/95">
              <p>
                By participating, you agree to our{" "}
                <a
                  href="https://www.fluidityiq.com/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary underline underline-offset-2"
                >
                  Terms of Service
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>{" "}
                and{" "}
                <a
                  href="https://www.fluidityiq.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary underline underline-offset-2"
                >
                  Privacy Policy
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
                .
              </p>
            </CardContent>
          </Card>

          {/* Agreement Section - Part of scroll content */}
          <div className="space-y-4 pb-safe-or-8 pt-2">
            <button
              type="button"
              role="checkbox"
              aria-checked={isChecked}
              onClick={() => setIsChecked(!isChecked)}
              className="flex w-full cursor-pointer items-start gap-3 rounded-lg bg-white/10 p-4 text-left backdrop-blur-sm transition-colors hover:bg-white/15"
            >
              <div
                aria-hidden="true"
                className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                  isChecked
                    ? "border-primary bg-primary"
                    : "border-white/50 bg-transparent"
                }`}
              >
                {isChecked && <Check className="h-4 w-4 text-primary-foreground" strokeWidth={3} />}
              </div>
              <span className="text-lg font-semibold leading-relaxed tracking-wide text-white/95">
                I understand the rules and agree to the Terms of Service
              </span>
            </button>

            <Button
              onClick={handleContinue}
              disabled={!isChecked || isSubmitting}
              className="w-full py-6 text-lg font-semibold"
              size="lg"
            >
              {isSubmitting ? "Please wait..." : "Continue"}
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll indicator pill */}
      {!hasScrolledToBottom && (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            scrollContainerRef.current?.scrollTo({
              top: scrollContainerRef.current.scrollHeight,
              behavior: "smooth",
            });
          }}
          className="absolute bottom-6 right-6 rounded-full bg-white/20 text-base font-semibold text-white backdrop-blur-sm hover:bg-white/30"
        >
          See more
          <ChevronDown className="h-5 w-5" />
        </Button>
      )}
    </div>
  );
}
